import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { League, ManagerLeaguesProps } from "../types";
import { useTeamContext } from "../contexts/TeamContext";
import { useNavigate } from "react-router-dom";
import ErrorBanner from "./UI/ErrorBanner";



export default function ManagerLeagues({ fplId }: ManagerLeaguesProps) {
  const { setLeagueId } = useTeamContext();
  const [loading, setLoading] = useState(false);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchManagerLeagues = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/api/leagues/${fplId}`);
        const nonDefaultManagerLeagues = response.data.leagues.classic.filter((league: League ) => league.admin_entry !== null);
        setLeagues(nonDefaultManagerLeagues);
      } catch (err) {
        console.error("Error fetching leagues", err);
        setError("Failed to load your leagues. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (fplId) fetchManagerLeagues();
  }, [fplId, API_BASE_URL]);

  const handleSelectLeague = (leagueId: number) => {
    setLeagueId(String(leagueId));
    navigate("/standings");
  };

  return (
    <div className="mx-auto max-w-2xl bg-surface text-content backdrop-blur-xl shadow-2xl rounded-2xl border border-line">
      <div className="border-b border-line p-6">
        <h1 className="text-center text-3xl md:text-4xl font-semibold tracking-tight">
          Your Leagues
          <span className="block text-base md:text-lg font-normal text-content-secondary mt-2">
            FPL ID: {fplId}
          </span>
        </h1>
      </div>

      <div className="p-6 md:p-8">
        {loading && (
          <p className="text-center text-content-secondary animate-pulse">Loading leagues...</p>
        )}

        {error && <ErrorBanner message={error} />}

        {!loading && !error && leagues.length === 0 && (
          <p className="text-center text-content-secondary">
            No leagues found for this manager.
          </p>
        )}

        {!loading && leagues.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            {leagues.map((league) => (
              <div
                key={league.id}
                className="flex items-center justify-between bg-surface-raised p-4 rounded-lg shadow-sm border border-line hover:bg-surface-sunken transition-colors"
              >
                <div>
                  <p className="font-medium text-content text-lg">{league.name}</p>
                  <p className="text-content-secondary text-sm">League ID: {league.id}</p>
                </div>

                <button
                  onClick={() => handleSelectLeague(league.id)}
                  className="inline-flex min-h-11 items-center gap-2 px-4 py-2 bg-inverse text-inverse-content rounded-lg font-medium hover:opacity-90 transition"
                >
                  Select <ArrowRight size={18} />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
